import {CalendarStore, DateRange} from "@/store/CalendarStore.ts";
import {EventColor} from "@t/fd/editor/EventColor.ts";
import {mondayDate} from "@t/util/DateUtil.ts";

/** Display constants used to show event block */
export const DISPLAY_SUBJECT = 'S';
export const DISPLAY_PLACE = 'P';
export const DISPLAY_CUSTOMER = 'X';
export const DISPLAY_DESCRIPTION = 'D';


export interface LayerElement {
	id: string;
}


export interface LayerButtonElement extends LayerElement {
	icon?: string;
	title?: string;
	/** If the width of the canvas is less than given number, label will be dropped (only if there is an icon) */
	dropLabel?: number;
	hover?: boolean;	// mouse is hovering over the button
	active?: boolean;	// button is pressed down
	selected?: boolean;		// button displays as selected
}



export type CalendarViewName = "months"	// full year view (month x month grid)
	| "weeks"							// 1-12 weeks
	| "days"							// 1-14 days
	| "list"							// chronological list of events
;

export interface CalendarViewPermanentlySaved {
	/* Current active calendar view */
	current: CalendarViewName;
	/** Number of weeks displayed (default is 5) */
	weeks?: number;
	/** Number of days displayed (default is 1) */
	days?: number;
}

export interface CalendarViewSessionSaved {
	selected?: Date | number;	/* will be saved as timestamp */
}

export interface CalendarViewSaved extends CalendarViewPermanentlySaved, CalendarViewSessionSaved {
}

export interface CalendarView extends CalendarViewSaved {
	/** Optional (may be "") name to be used to save state of the calendar */
	saveName: string;

	/* List of all allowed views */
	enabled: CalendarViewName[];

	store: CalendarStore;

	/** Selected day */
	selected: Date;
	/** first day in the grid derived from selected */
	aligned?: Date;
	/** Visible date range */
	focused?: DateRange;

	display: string;			// display string "SPX", ... see CalendarSettings.java
	showAttendees: "no"|"mono"|"full";		// show attendees of the event
	showTime: boolean;			// show time in events
	autocomplete: boolean;		// init events for autocomplete
	eventAlarm:number,			// in ms
	wholeDayEventAlarm:number,	// in ms

	/** Number of hours displayed (default is 10) */
	hours?: number;

	/** Start / end hour of working day */
	startHour?: number;		// default 8
	endHour?: number;		// default 18

	snapMins?:number;		// if you want snapping when editing an event
	snapMonday?: boolean;
	defaultDurationMins?: number;	// default duration of event
	defaultColor?: number;
}

export function initFromSaved(initial: CalendarView) {
	if (initial.saveName) {
		const psaved = localStorage.getItem(initial.saveName + ".view");
		if (psaved) {
			const s = JSON.parse(psaved) as CalendarViewPermanentlySaved;
			if (s.current) initial.current = s.current;
			if (typeof s.weeks === 'number') initial.weeks = s.weeks;
			if (typeof s.days === 'number') initial.days = s.days;
		}
		const ssaved = sessionStorage.getItem(initial.saveName + ".view");
		if (ssaved) {
			const s = JSON.parse(ssaved) as CalendarViewSessionSaved;
			if (typeof s.selected === 'number') initial.selected = new Date(s.selected);
		} else if (initial.snapMonday && initial.days >= 0) {
			initial.selected = mondayDate(initial.selected);	// align to monday
		}
	}
}

export function savedSelectedDate(view:CalendarView):Date|null {
	if (view.saveName) {
		const saved = sessionStorage.getItem(view.saveName + ".view");
		if (saved) {
			const s = JSON.parse(saved) as CalendarViewSessionSaved;
			if (typeof s.selected === 'number') return new Date(s.selected);
		}
	}
	return null;
}

export function saveView(view: CalendarView, selectedDate?:Date):void {
	if (view.saveName) {
		const parmanentSave:CalendarViewPermanentlySaved = {
			current: view.current,
			weeks: view.weeks,
			days: view.days,
		}
		const sessionSave:CalendarViewSessionSaved = {
			selected: selectedDate ? selectedDate.getTime() : view.selected.getTime(),
		}

		localStorage.setItem(view.saveName + ".view", JSON.stringify(parmanentSave));
		sessionStorage.setItem(view.saveName + ".view", JSON.stringify(sessionSave));
	}
}


export function initialView(store:CalendarStore, saveName:string, display:string,  showAttendees:"no"|"mono"|"full",  minTime:number,  maxTime:number,
	 snapMinutes:number,  snapMonday: boolean, showTime:boolean,  autocomplete:boolean,  eventAlarm:number,  wholeDayEventAlarm:number):CalendarView {

	const view:CalendarView = {
		saveName,
		current: "days",
		enabled: [ "days", "weeks", "months" ],
		store,
		selected: new Date(),

		display,
		showAttendees,
		showTime,
		autocomplete,
		eventAlarm,
		wholeDayEventAlarm,

		weeks: 5,
		days: 3,
		startHour: minTime,
		endHour: maxTime,
		hours: Math.max(maxTime - minTime + 2, 4),

		snapMins: snapMinutes,
		snapMonday: snapMonday
	};
	initFromSaved(view);
	return view;
}
